export type BrowserCoordinates = {
  latitude: number;
  longitude: number;
};

export type ResolvedLocationDetails = {
  area: string;
  city: string;
  district: string;
  state: string;
  pincode: string;
  country: string;
  displayName: string;
  latitude: number;
  longitude: number;
};

export type LocationSearchResult = {
  placeId: string;
  displayName: string;
  area: string;
  city: string;
  state: string;
  pincode: string;
  latitude: number;
  longitude: number;
};

const firstNonEmpty = (...values: Array<string | undefined>) =>
  values.find((v) => typeof v === "string" && v.trim().length > 0)?.trim() ?? "";

export const requestCurrentPosition = () =>
  new Promise<BrowserCoordinates>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Location is not supported by this browser"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => reject(error),
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 },
    );
  });

export const reverseGeocodeCoordinates = async (
  latitude: number,
  longitude: number,
): Promise<ResolvedLocationDetails> => {
  const response = await fetch(
    `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&addressdetails=1`,
    { headers: { Accept: "application/json" } },
  );

  if (!response.ok) {
    throw new Error("Unable to resolve your delivery location.");
  }

  const data = (await response.json()) as {
    address?: Record<string, string | undefined>;
    display_name?: string;
  };

  const address = data.address ?? {};
  const area = firstNonEmpty(
    address.suburb,
    address.neighbourhood,
    address.city_district,
    address.residential,
    address.hamlet,
    address.village,
    address.town,
    address.road,
  );
  const city = firstNonEmpty(
    address.city,
    address.town,
    address.municipality,
    address.county,
    address.state_district,
  );
  const district = firstNonEmpty(
    address.state_district,
    address.county,
    address.district,
  );
  const state = firstNonEmpty(address.state);
  const pincode = firstNonEmpty(address.postcode);
  const country = firstNonEmpty(address.country);
  const displayName = firstNonEmpty(
    [area, city, state].filter(Boolean).join(", "),
    data.display_name,
  );

  return {
    area,
    city,
    district,
    state,
    pincode,
    country,
    displayName,
    latitude,
    longitude,
  };
};

export const searchLocations = async (
  query: string,
): Promise<LocationSearchResult[]> => {
  const trimmed = query.trim();
  if (trimmed.length < 2) return [];

  const response = await fetch(
    `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(trimmed)}&countrycodes=in&addressdetails=1&limit=8`,
    { headers: { Accept: "application/json" } },
  );

  if (!response.ok) return [];

  const data = (await response.json()) as Array<{
    place_id: number;
    display_name: string;
    lat: string;
    lon: string;
    address?: Record<string, string | undefined>;
  }>;

  return data.map((item) => {
    const address = item.address ?? {};
    const area = firstNonEmpty(
      address.suburb,
      address.neighbourhood,
      address.city_district,
      address.road,
      address.hamlet,
      address.village,
    );
    const city = firstNonEmpty(
      address.city,
      address.town,
      address.municipality,
      address.county,
    );
    const state = firstNonEmpty(address.state);
    const pincode = firstNonEmpty(address.postcode);

    return {
      placeId: String(item.place_id),
      displayName: item.display_name,
      area,
      city,
      state,
      pincode,
      latitude: parseFloat(item.lat),
      longitude: parseFloat(item.lon),
    };
  });
};

export const getLocationErrorMessage = (error: unknown) => {
  if (
    typeof window !== "undefined" &&
    error instanceof GeolocationPositionError
  ) {
    if (error.code === error.PERMISSION_DENIED) {
      return "Allow location access in your browser, or search for your area.";
    }
    if (error.code === error.POSITION_UNAVAILABLE) {
      return "Unable to detect your location right now.";
    }
    if (error.code === error.TIMEOUT) {
      return "Location request timed out. Please try again.";
    }
  }

  if (error instanceof Error) return error.message;
  return "Unable to get your current location.";
};
